import React from 'react'
import { useIntl } from 'react-intl'
import { getAssetUrl } from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {
  productUrlBuilder,
  Order as OrderModel,
  OrderProductLineItem,
  PRODUCT_STATUS
} from 'msp-integrations'
import { CustomLinkComponent } from 'msp-components'

import translations from './OrderProductItemsList.constants'
import * as S from './OrderProductItemsList.styles'

const OrderProductItemsList = ({ order }: { order: OrderModel }) => {
  const { formatMessage, formatNumber } = useIntl()
  const fallBackImage = getAssetUrl('static/img/fallback-product-image.png')
  const handleImageNotFound = (el: HTMLImageElement) => {
    if (el.src !== fallBackImage) {
      el.src = fallBackImage
    }
  }

  const OrderProductItemsListItem = ({
    productItem
  }: {
    productItem: OrderProductLineItem
  }) => {
    return (
      <CustomLinkComponent
        className={S.ProductLineItemWrapper}
        href={productUrlBuilder({
          id: productItem.productId,
          name: productItem.productName,
          classCategoryName: productItem.c_classCategoryName,
          primaryCategoryName: productItem.c_primaryCategoryName,
          classCategoryPath: productItem.c_classCategoryPath,
          primaryCategoryPath: productItem.c_primaryCategoryPath
        })}
        uppercase={false}
        isDisabled={!productItem.c_purchasable}
        inline
      >
        <div className={S.ProductLineItemArticleWrapper}>
          <div className={S.ProductLineItemArticleImageWrapper}>
            <div className={S.ProductLineItemArticleImageContainer}>
              {productItem.c_image && (
                <img
                  className={S.ProductLineItemArticleImage}
                  src={productItem.c_image}
                  onError={(ev) =>
                    handleImageNotFound(ev.target as HTMLImageElement)
                  }
                  alt={productItem.productName}
                />
              )}
              {!productItem?.c_image && (
                <img
                  src={fallBackImage}
                  alt={productItem.productName}
                  className={S.ProductLineItemArticleImage}
                />
              )}
            </div>
          </div>
          <div className={S.ProductLineItemArticleDetailsWrapper}>
            <div className={S.ProductLineItemArticleName}>
              {productItem.productName}
            </div>
            <div className={S.ProductLineItemArticleDescription}>
              {productItem.c_servings || ''}
            </div>
            <div className={S.ProductLineItemArticleNumber}>
              {formatMessage(translations.articleNumber, {
                articleNo: productItem.productId
              })}
            </div>
          </div>
        </div>
        <div className={S.ProductLineItemTableQuantity}>
          {productItem.quantity}
        </div>
        <div className={S.ProductLineItemTableUnitPrice}>
          {formatNumber(productItem.basePrice, {
            style: 'currency',
            currency: order.currency,
            currencyDisplay: 'code'
          })}
        </div>
        <div className={S.ProductLineItemTableProductStatus}>
          {formatMessage(
            translations[
              PRODUCT_STATUS[productItem.c_productStatus || 'NOT_SHIPPED']
            ]
          )}
        </div>
        <div className={S.ProductLineItemTableTotal}>
          <div
            className={
              productItem.priceAfterItemDiscount !== productItem.price
                ? S.ProductLineItemTableTotalBasePrice
                : ''
            }
          >
            {formatNumber(productItem.price, {
              style: 'currency',
              currency: order.currency,
              currencyDisplay: 'code'
            })}
          </div>
          {productItem.priceAfterItemDiscount !== productItem.price && (
            <>
              {productItem.priceAdjustments.map((priceAdjustment) => (
                <div
                  key={priceAdjustment.priceAdjustmentId}
                  className={S.ProductLineItemTableTotalPromoPrice}
                >
                  {formatNumber(priceAdjustment.price, {
                    style: 'currency',
                    currency: order.currency,
                    currencyDisplay: 'code'
                  })}
                </div>
              ))}
              <div className=''>
                {formatNumber(productItem.priceAfterItemDiscount, {
                  style: 'currency',
                  currency: order.currency,
                  currencyDisplay: 'code'
                })}
              </div>
            </>
          )}
        </div>

        <div className={S.ProductLineItemDetailsWrapper}>
          <div className={S.ProductLineItemQuantityStatusWrapper}>
            <div className={S.ProductLineItemStatusWrapper}>
              {formatMessage(translations.productStatus, {
                status: formatMessage(
                  translations[
                    PRODUCT_STATUS[productItem.c_productStatus || 'NOT_SHIPPED']
                  ]
                )
              })}
            </div>
            <div className={S.ProductLineItemQuantityWrapper}>
              {productItem.quantity} x{' '}
              {formatNumber(productItem.basePrice, {
                style: 'currency',
                currency: order.currency,
                currencyDisplay: 'code'
              })}
            </div>
          </div>
          <div className={S.ProductLineItemPriceInfoWrapper}>
            <div
              className={
                productItem.priceAfterItemDiscount !== productItem.price
                  ? S.ProductLineItemTableTotalBasePrice
                  : ''
              }
            >
              {formatNumber(productItem.price, {
                style: 'currency',
                currency: order.currency,
                currencyDisplay: 'code'
              })}
            </div>
            {productItem.priceAfterItemDiscount !== productItem.price && (
              <>
                {productItem.priceAdjustments.map((priceAdjustment) => (
                  <div
                    key={priceAdjustment.priceAdjustmentId}
                    className={S.ProductLineItemTableTotalPromoPrice}
                  >
                    {formatNumber(priceAdjustment.price, {
                      style: 'currency',
                      currency: order.currency,
                      currencyDisplay: 'code'
                    })}
                  </div>
                ))}
                <div className=''>
                  {formatNumber(productItem.priceAfterItemDiscount, {
                    style: 'currency',
                    currency: order.currency,
                    currencyDisplay: 'code'
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </CustomLinkComponent>
    )
  }

  return (
    <>
      <div className={S.Wrapper}>
        <div className={S.TableViewHeadWrapper}>
          <div className={S.TableViewArticleName}>
            {formatMessage(translations.tableArticle)}
          </div>
          <div className={S.TableViewQuantity}>
            {formatMessage(translations.tableQuantity)}
          </div>
          <div className={S.TableViewUnitPrice}>
            {formatMessage(translations.tableUnitPrice)}
          </div>
          <div className={S.TableViewProductStatus}>
            {formatMessage(translations.tableStatus)}
          </div>
          <div className={S.TableViewTotal}>
            {formatMessage(translations.tableTotal)}
          </div>
        </div>
        {order.productItems.map((productItem, index) => (
          <OrderProductItemsListItem
            productItem={productItem}
            key={`order-product-${productItem.productId}-${index}`}
          />
        ))}
        {order.giftCertificateItems.map((giftCertificateItem, index) => {
          let productItem = {
            itemId: giftCertificateItem.giftCertificateItemId,
            productId: giftCertificateItem.c_originalSKU,
            productName: giftCertificateItem.c_productName,
            c_image: giftCertificateItem.c_image,
            c_inventory: giftCertificateItem.c_inventory,
            c_purchasable: giftCertificateItem.c_purchasable,
            inventory: giftCertificateItem.c_inventory,
            quantity: 1,
            basePrice: giftCertificateItem.amount,
            price: giftCertificateItem.amount,
            priceAfterItemDiscount: giftCertificateItem.amount,
            priceAfterOrderDiscount: giftCertificateItem.amount,
            itemText: ''
          }

          return (
            <OrderProductItemsListItem
              productItem={productItem}
              key={`order-product-${productItem.productId}-${index}`}
            />
          )
        })}
      </div>
    </>
  )
}

export default OrderProductItemsList
